


import { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Tabs,
  Tab,
  Typography,
  Tooltip,
  Drawer,
  Stack,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import ToastMsg from '../../../Components/ToastMsg/ToastMsg';
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import { ContentInTooltip, formatDateAndTimeMeth2 } from "../../../Components/CommonFunctions/CommonFunctions";
import { CommonGridToolBarWithFilterText, NewCommonPatientDetails } from "../../../Components/Common Components/CommonComponents";
import { ImagePaths } from "../../../Utility/ImagePaths";
import CommonDatePicker from "../../../Components/Common Components/CommonDatePicker";
import { DateTime } from "luxon";
import Loader from '../../../Components/Loader'
import CommonValidation from "../../../Components/CommonFunctions/CommonValidation";


const ReverseSpecimen = () => {
  const [tabValue, setTabValue] = useState(0);
  //Current tab status
  const [currentUhid, setCurrentUhid] = useState('');
  const [currentMobileNumber, setCurrentMobileNumber] = useState("");
  const [currentipNum, setCurrentIpNum] = useState("");

  //Log tab status  
  const [loguhid, setLogUhid] = useState("");
  const [logMobileNumber, setLogMobileNumber] = useState("");
  const [logipNum, setLogIpNum] = useState("");

  //Data
  const [data, setData] = useState([]);
  const [logData, setLogData] = useState([]);
  const [logLoaded, setLogLoaded] = useState(false);

  // Toast message states
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState('');
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState('');

  //Pagination
  const [page, setpage] = useState(0);
  const [pageSize, setpageSize] = useState(10);

  //dateFilter
  const [FromDate, setFromDate] = useState('');
  const [ToDate, setToDate] = useState('');
  const [DateIndex, setDateIndex] = useState(0);



  //loader
  const [isLoading, setIsLoading] = useState(false)

  // --- NEW: Ref to track mounted status ---
  const isMounted = useRef(true);

  // Function to close toast messages
  const msgClose = () => {
    setIsErrorMsg(false);
    setIsErrorMsgText('');
    setSuccessMsg(false);
    setSuccessMsgText('');
  };

  // Helper function to show error toast
  const showErrorToast = (message) => {
    setIsErrorMsgText(message);
    setIsErrorMsg(true);
    setSuccessMsg(false); // Ensure success toast is hidden
  };

  // Helper function to show success toast
  const showSuccessToast = (message) => {
    setSuccessMsgText(message);
    setSuccessMsg(true);
    setIsErrorMsg(false); // Ensure error toast is hidden
  };

  // Drawer state management
  const [filterOpen, setFilterOpen] = useState(false);

  // Drawer handlers
  const handleOpenFilter = () => setFilterOpen(true);
  const handleCloseFilter = () => setFilterOpen(false);


  useEffect(() => {
    fetchCurrentData();
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchCurrentData = async (params = {}) => {
    let { patient_account_number, mobile_number } = params;
    msgClose();
    setIsLoading(true)
    try {
      const response = await RestAPIService.getAll(Serviceurls.CURRENT_API_URL_LAB + `?get_type=${"laboratory_specimen"}&uhid=${patient_account_number ? patient_account_number : ""}&mobile_number=${mobile_number ? mobile_number : ""}`)
      if (!isMounted.current) return;
      if (response.data.status === 'success') {
        setData(response.data.data || []);
        setFilterOpen(false)
      } else {
        showErrorToast(response.data.message);
        setData([]);
      }
    } catch (e) {
      if (!isMounted.current) return;
      showErrorToast(e.message);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }

  const fetchLogData = async (params = {}) => {
    let { patient_account_numberL, mobile_numberL } = params;
    msgClose();
    setIsLoading(true);
    try {
      const response = await RestAPIService.getAll(Serviceurls.LOG_API_URL_LAB + `?get_type=${"laboratory_specimen"}&uhid=${patient_account_numberL ? patient_account_numberL : ""}&mobile_number=${mobile_numberL ? mobile_numberL : ""}&from_date=${FromDate || ""}&to_date=${ToDate || ""}`)
      if (!isMounted.current) return;
      if (response.data.status === "success") {
        setLogData(response.data.data || []);
        setFilterOpen(false)
      } else {
        showErrorToast(response.data.message);
        setLogData([]);
      }
      setLogLoaded(true);
    } catch (e) {
      if (!isMounted.current) return;
      showErrorToast(e.message);
      setLogData([]);
      setLogLoaded(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (e, newValue) => {
    setTabValue(newValue);
  };


  useEffect(() => {
    if (tabValue === 1 && !logLoaded) {
      fetchLogData();
    }
    // When switching tabs, clear search fields for the other tab
    if (tabValue === 0) {
      setLogUhid('');
      setLogMobileNumber('');
    } else { // tabValue === 1
      setCurrentUhid('');
      setCurrentMobileNumber('');
      setCurrentIpNum('');
    }
  }, [tabValue, logLoaded]);

  const handleSearch = async () => {
    try {
      let params = {
        //CURRENT TAB
        patient_account_number: currentUhid.trim(),
        mobile_number: currentMobileNumber.trim(),
        //LOG TAB
        patient_account_numberL: loguhid.trim(),
        mobile_numberL: logMobileNumber.trim()
      };

      if (tabValue === 0) {
        await fetchCurrentData(params);
      } else {
        await fetchLogData(params);
      }
    } catch (err) {
      if (!isMounted.current) return;
      showErrorToast(err.message);
      if (tabValue === 0) setData([]);
      else setLogData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReverseDischarge = async (rowData) => {
    setIsLoading(true);
    const payload = {
      revert_id: rowData.revert_id,
      revert_type: "laboratory_specimen",
    };
    try {
      const response = await RestAPIService.create(payload, Serviceurls.PERFORM_LAB_URL);
      if (!isMounted.current) return;
      if (response.data.status === "success") {
        showSuccessToast(response?.data?.message)
        fetchCurrentData({}, true);
      } else {
        showErrorToast(response.data.message);
      }
    } catch (error) {
      if (!isMounted.current) return;
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const columnsCurrent = [
    {
      field: "patient_name",
      headerName: "Patient Details",
      flex: 0.22,
      renderCell: (params) => {
        return (
          <NewCommonPatientDetails data={params.row} />
        );
      },
    },
    {
      field: "source_id ", headerName: "Admission No", flex: 0.16,
      renderCell: (params) => (<Box component={'div'}>
        {params?.row?.source_id ? params?.row?.source_id?.length > 22 ?
          <Tooltip placement='top' title={params?.row?.ip_number} arrow>
            <div>{"..." + params?.row?.source_id.slice(5)}</div></Tooltip> :
          params?.row?.source_id : "-"}</Box>)

    },
    {
      field: "patient_type", headerName: "Patient Type", flex: 0.16,
      renderCell: (params) => {
        const { patient_type } = params.row || "-";
        return (
          <Box display="flex" alignItems="center" gap={1} justifyContent={"center"}>
            <Box>
              <Typography>{ContentInTooltip(patient_type, 14)} </Typography>
            </Box>
          </Box>
        );
      },

    },
    {
      field: "created_by", headerName: "Request Details", flex: 0.16,
      renderCell: (params) => (
        <Box>
          <Typography fontSize={"0.8vw"} fontWeight={600}>{
            <div style={{ fontSize: '1vw' }}>{params?.row?.created_by?.length > 15 ?
              <Tooltip placement="top" title={params?.row?.created_by} arrow><div>{params?.row?.created_by.slice(0, 15) + "..."}</div></Tooltip>
              : params?.row?.created_by || "-"}</div>
          }</Typography>
          <Typography fontSize={"0.8vw"}>{(params?.row?.created_at ? formatDateAndTimeMeth2(params?.row?.created_at) : "-")}</Typography>
        </Box>)
    },
    {
      field: "remarks", headerName: "Reason", flex: 0.16,
      renderCell: (params) => {
        const { remarks } = params.row || {};
        return (
          <Box display="flex" alignItems="center" gap={1} justifyContent={"center"}>
            <Box>
              <Typography >{ContentInTooltip(remarks, 15) || '-'} </Typography>

            </Box>
          </Box>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      headerAlign: "center",
      align: "center",
      flex: 0.10,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => handleReverseDischarge(params.row)}
          disabled={isLoading}
          justifyContent={"center"}
        >
          Revert
        </Button>
      ),
    },
  ];

  const columnsLog = [
    {
      field: "patientDetails",
      headerName: "Patient Details",
      flex: 0.18,
      renderCell: (params) => {
        return (
          <NewCommonPatientDetails data={params.row} />
        );
      },
    },
    {
      field: "source_id", headerName: "Admission No", flex: 0.14,
      renderCell: (params) => (<Box component={'div'}>
        {params?.row?.source_id ? params?.row?.source_id?.length > 17 ?
          <Tooltip placement='top' title={params?.row?.source_id} arrow>
            <div>{"..." + params?.row?.source_id.slice(5)}</div></Tooltip> :
          params?.row?.source_id : "-"}</Box>)
    },
    {
      field: "patient_type", headerName: "Patient Type", flex: 0.14,
      renderCell: (params) => {
        const { patient_type } = params.row || {};
        return (
          <Box display="flex" alignItems="center" gap={1} justifyContent={"center"}>
            <Box>
              <Typography >{patient_type || '-'} </Typography>

            </Box>
          </Box>
        );
      },
    },
    {
      field: "remarks", headerName: "Reason", flex: 0.14,
      renderCell: (params) => {
        const { remarks } = params.row || {};
        return (
          <Box display="flex" alignItems="center" gap={1} justifyContent={"center"}>
            <Box>
              <Typography >{ContentInTooltip(remarks, 15) || '-'} </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "created_at", headerName: "Request Details", flex: 0.13,
      renderCell: (params) => (
        <Box>
          <Typography fontSize={"0.8vw"} fontWeight={600}>{
            <div style={{ fontSize: '0.8vw' }}>{params?.row?.created_by?.length > 15 ?
              <Tooltip placement="top" title={params?.row?.created_by} arrow><div>{params?.row?.created_by.slice(0, 15) + "..."}</div></Tooltip>
              : params?.row?.created_by || "-"}</div>
          }</Typography>
          <Typography fontSize={"0.8vw"}>{(params?.row?.created_at ? formatDateAndTimeMeth2(params?.row?.created_at) : "-")}</Typography>
        </Box>)
    },
    {
      field: "approved_by", headerName: "Approver Details", flex: 0.13,
      renderCell: (params) => (
        <Box>
          <Typography fontSize={"0.8vw"} fontWeight={600}>{
            <div style={{ fontSize: '0.8vw' }}>{params?.row?.approved_by?.length > 15 ?
              <Tooltip placement="top" title={params?.row?.approved_by} arrow><div>{params?.row?.approved_by.slice(0, 15) + "..."}</div></Tooltip>
              : params?.row?.approved_by || "-"}</div>
          }</Typography>
          <Typography fontSize={"0.8vw"}>{(params?.row?.approved_at ? formatDateAndTimeMeth2(params?.row?.approved_at) : "-")}</Typography>
        </Box>)
    },
  ];

  const gridToolBar = () => {
    return (
      <Box>
        <CommonGridToolBarWithFilterText />
      </Box>
    )
  }

  const handleDateChange = (fromDate, toDate, dateIndex) => {
    const today = DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd');
    setFromDate(dateIndex === 1 ? today : fromDate);
    setToDate(dateIndex === 1 ? today : toDate);
    setDateIndex(dateIndex);
  };

  const Filter = () => {
    return (
      <Box component={"div"} className='eMed_Filter_Billing_Container'>
        <Box component={"div"} className='eMed_Filter_Top'>
          <Typography variant='h6' fontWeight={"600"}>{"Filter"}</Typography>
          <Button onClick={() => { handleCloseFilter() }} className='eMed_usrconf_btn'>
            <img src={ImagePaths.LabCloseButton.default} alt="close" className='eMed_action_img_small' />
          </Button>
        </Box>
        <br></br>
        <Box component={"div"} >
          {tabValue === 0 ? (
            <Box display="flex" gap={2} mb={2}>
              <TextField
                label="UHID"
                size="small"
                value={currentUhid}
                onChange={(e) => setCurrentUhid(e.target.value)}
                disabled={isLoading}
                sx={{ marginLeft: '1vw' }}
                inputProps={{ maxLength: 25 }}
              />
              <TextField
                label="Mobile Number"
                size="small"
                value={currentMobileNumber}
                onChange={(e) => {
                  const value = e.target.value;
                  if (CommonValidation.numberOnly(value) || (value === "")) {
                    setCurrentMobileNumber(e.target.value)
                  }
                }}
                disabled={isLoading}
                inputProps={{ maxLength: 10 }}
              />
            </Box>
          ) : (
            <Box display="flex" flexDirection={'column'} justifyContent={'flex-end'} gap={2} mb={2} alignItems={'flex-end'} marginRight={"1vw"}>
              <Box style={{ width: "40%", }} component={"div"}>
                <CommonDatePicker
                  FromDate={FromDate}
                  ToDate={ToDate}
                  DateIndex={DateIndex}
                  SelectedDates={handleDateChange}
                />
              </Box>
              <Box display="flex" flexDirection={'row'} gap={2} mb={2} sx={{ marginLeft: '1vw' }}>
                <TextField
                  label="UHID"
                  size="small"
                  value={loguhid}
                  onChange={(e) => setLogUhid(e.target.value)}
                  disabled={isLoading}
                  inputProps={{ maxLength: 25 }}
                />
                <TextField
                  label="Mobile Number"
                  size="small"
                  value={logMobileNumber}
                  onChange={(e) => {
                  const value = e.target.value;
                  if (CommonValidation.numberOnly(value) || (value === "")) {
                    setLogMobileNumber(e.target.value)
                  }
                  }}
                  disabled={isLoading}
                  inputProps={{ maxLength: 10 }}
                />
              </Box>
            </Box>
          )}

          {/* clear and search button */}
          <Box component={"div"} className="eMed_Filter_Btn_Div">
            <Stack direction="row" spacing={2} mt={2}>

              <Button variant="outlined" onClick={() => {
                setLogUhid('');
                setLogMobileNumber('')
                setLogIpNum('');
                msgClose();
                setLogLoaded(false);
                setCurrentUhid('');
                setCurrentMobileNumber('')
                setCurrentIpNum('');
                setFromDate('');
                setToDate('');
                setDateIndex(0);
                msgClose();
                fetchCurrentData();
              }} disabled={isLoading} sx={{textTransform: "capitalize"}}>
                Clear
              </Button>
              <Button variant="contained" sx={{textTransform: "capitalize"}} onClick={handleSearch} disabled={isLoading}>
                Search
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    )
  };

  return (
    <Box>
      {/* Toast Messages */}
      {isErrorMsg ?
        <ToastMsg
          severity={'error'}
          msg={isErrorMsgText}
          msgPop={msgClose} /> : null}
      {!isErrorMsg && successMsg &&
        <ToastMsg
          severity={'success'}
          msg={successMsgText}
          msgPop={msgClose} />}


      <Box display={'flex'} justifyContent={'space-between'}>
        {/* Tabs */}
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Pending" disabled={isLoading} sx={{ textTransform: 'capitalize', minHeight: '1vw', fontWeight: 600 }} />
          <Tab label="Completed" disabled={isLoading} sx={{ textTransform: 'capitalize', minHeight: '1vw', fontWeight: 600 }} />
        </Tabs>

        <Box component="div" className="eMed_Filter_Icon_div" sx={{ marginTop: "1dvh" }}>
          <Tooltip title="Filter" placement="top" arrow>
            <Button className="eMed_usrconf_btn" onClick={handleOpenFilter}>
              <img src={ImagePaths.Filter.default} alt="filter" className="eMed_action_img" />
            </Button>
          </Tooltip>
        </Box>

      </Box>

      {/* Loader display */}
      {/* {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
          <Typography ml={2}>Loading data...</Typography>
        </Box>
      ) : ( */}
      <Loader loaderOpen={isLoading} />
        <div style={{ height: '72dvh', width: '100%' }}>
          {/* DataGrid */}
          <DataGrid
            rows={tabValue === 0 ? data : logData}
            columns={tabValue === 0 ? columnsCurrent : columnsLog}
            getRowId={(row) => row.revert_id || row.id || row.ip_admission_id || (row.uhid + row.ip_number) || JSON.stringify(row)}
            getRowHeight={() => 70}
            density="compact"
            onPageChange={(newPage) => { setpage(newPage) }}
            onPageSizeChange={(newPageSize) => { setpageSize(newPageSize) }}
            rowsPerPageOptions={[10, 20, 30]}
            page={page}
            pageSize={pageSize}
            localeText={{
              toolbarColumns: "",
              toolbarDensity: "",
              toolbarExport: "",
              toolbarFilters: "",
              toolbarExportPrint: ""
            }}
            components={{
              Toolbar: gridToolBar,
              NoRowsOverlay: () => (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  {'No Records To Be Shown'}
                </Box>
              )
            }}
          />
        </div>
      {/* )} */}

      {/* Drawer for Filter */}
      <Drawer
        anchor="right"
        open={filterOpen}
        onClose={handleCloseFilter}
      >
        {Filter()}
      </Drawer>

    </Box>
  );
};

export default ReverseSpecimen;
